/**
 * Ink colours for anything drawn ON TOP of a rendered PDF page.
 *
 * Field overlays are positioned over a canvas that pdf.js paints from the
 * document itself — which is white paper in every theme, because a PDF has no
 * idea the app has a dark mode. So these surfaces must NOT inherit
 * `text-foreground` from the app shell. In dark mode that resolves to pure
 * white (`--foreground: 0 0% 100%`), and the result is white text on white
 * paper, legible only where a 12%-alpha tint happens to sit behind it.
 *
 * That is why these are fixed hex values and not theme tokens. It is the same
 * reasoning `BrandedSigningFrame` uses to derive its header text from the
 * brand colour rather than the theme: when the background is not the app's
 * background, the app's foreground is the wrong answer.
 *
 * They also match the artifact. The sealed PDF is black on white whatever the
 * signer's theme, so a field that reads as dark ink on screen is showing them
 * what they are actually about to sign.
 */

/** Body text in a field overlay. Near-black rather than pure black, matching
 *  the app's own light-mode foreground (`0 0% 4%`). */
export const PAPER_INK = '#0a0a0a';

/**
 * Placeholders and secondary marks. Placeholders are the worst-affected case:
 * browsers already render them at reduced opacity, so an inherited white
 * placeholder on white paper is invisible rather than merely faint.
 */
export const PAPER_INK_MUTED = '#52525b';

/** Tailwind classes for the placeholder colour above — `::placeholder` cannot
 *  be set from an inline style object. `placeholder:opacity-100` overrides the
 *  UA's own dimming, which would otherwise re-fade the colour we just set. */
export const PAPER_PLACEHOLDER_CLASS = 'placeholder:text-zinc-600 placeholder:opacity-100';

/** Largest text in a field overlay — the size the desktop layout was drawn at. */
export const FIELD_TEXT_MAX_PX = 12;
/** Smallest before it stops being readable at arm's length. */
export const FIELD_TEXT_MIN_PX = 8;

/**
 * Text size for a field overlay, from the height of the box it sits in.
 *
 * Field boxes are a FRACTION of the rendered page (`field.h * rendered.height`),
 * so they shrink with the viewport — from a 1024px-wide page down to whatever a
 * phone gives. The text was a fixed 12px, so at portrait widths the boxes
 * collapsed around type that never moved, and values overflowed their borders.
 *
 * Half the box height leaves room for the 4px padding above and below without
 * the descenders touching the border. Clamped at both ends: never larger than
 * the desktop size, never smaller than legible — a field too short for 8px text
 * is a placement problem in the document, and shrinking the type further would
 * hide it rather than show it.
 */
export function fieldTextSize(boxHeightPx: number): number {
  if (!Number.isFinite(boxHeightPx) || boxHeightPx <= 0) return FIELD_TEXT_MAX_PX;
  return Math.round(Math.max(FIELD_TEXT_MIN_PX, Math.min(FIELD_TEXT_MAX_PX, boxHeightPx * 0.5)));
}

/**
 * Padding inside a field overlay. Scales with the box for the same reason, and
 * disappears entirely on a very short one, where 4px a side is most of the
 * available height.
 */
export function fieldTextPadding(boxHeightPx: number): number {
  if (!Number.isFinite(boxHeightPx) || boxHeightPx <= 0) return 4;
  return Math.max(1, Math.min(4, Math.round(boxHeightPx * 0.12)));
}
